import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Footer from 'modules/Footer';
import Header from 'modules/Header';
import Navigation from 'modules/Navigation';
import { useNavigationRail } from 'modules/Navigation/_utils';
import { useNewUi, useMediaExportProgress } from 'hooks';
import { isEmpty } from 'lodash';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { ProgressBar } from 'ui-kit';
import { notification } from 'utils/services';
import { saveIdToLocalStorage, isIdInLocalStorage } from 'utils/custom';
import { AuthEntity, RetailerEntity, UserEntity } from '_entities';

import * as Styled from './styles';

const { getUsersImportInfo, getRetailersImportInfo, getAuthenticatedUser } = AuthEntity.selectors;
const { getRetailersImportProgress, resetRetailerImportData, setRetailerToastId } = RetailerEntity.actions;
const { getUsersImportProgress, resetUserImportInfo, setUserToastId } = UserEntity.actions;
const { getLastRetailerImport, getRetailerToastId } = RetailerEntity.selectors;
const { getLastUserImport, getUserToastId } = UserEntity.selectors;

const PROGRESS_STATES = ['VALIDATION_FAILED', 'SUCCESS', 'FAILED'];

function PageContainer({ children, preventNotificationDismiss, styles }) {
  const isAuthenticated = useSelector(getAuthenticatedUser);
  const retailersImportInfo = useSelector(getRetailersImportInfo);
  const usersImportInfo = useSelector(getUsersImportInfo);
  const lastRetailerImport = useSelector(getLastRetailerImport);
  const lastUserImport = useSelector(getLastUserImport);
  const retailerToastId = useSelector(getRetailerToastId);
  const userToastId = useSelector(getUserToastId);
  const [isUserProgressOpen, setIsUserProgressOpen] = useState(!isIdInLocalStorage(usersImportInfo?.data));
  const [isRetailerProgressOpen, setIsRetailerProgressOpen] = useState(!isIdInLocalStorage(retailersImportInfo?.data));
  const userIntervalIdRef = useRef(0);
  const retailerIntervalIdRef = useRef(0);
  const dispatch = useDispatch();
  const newUi = useNewUi();
  const { collapsed: railCollapsed } = useNavigationRail();
  // Media zip export progress (P20M-1319) — same top-bar widget as the CSV imports.
  const { importInfo: mediaExportInfo, onClose: handleCloseMediaExport } = useMediaExportProgress(isAuthenticated);

  const handleCloseUserProgressBar = async (id, toastId) => {
    if (!id) return;
    if (id) saveIdToLocalStorage(+id);
    setIsUserProgressOpen(false);
    toast.dismiss(toastId || userToastId);
    await dispatch(resetUserImportInfo());
  };

  const handleCloseRetailerProgressBar = async (id, toastId) => {
    if (!id) return;
    if (id) saveIdToLocalStorage(+id);
    setIsRetailerProgressOpen(false);
    toast.dismiss(toastId || retailerToastId);
    await dispatch(resetRetailerImportData());
  };

  const checkImportStatus = async (getImportProgress, setIsProgressOpen, importType) => {
    try {
      const response = await dispatch(getImportProgress());
      const dataArray = response?.data || [];
      const dataArrayIds = dataArray.map(({ id }) => Number(id));
      const lastImportIndex = dataArrayIds.indexOf(Math.max(...dataArrayIds));
      const attributes = dataArray[lastImportIndex]?.attributes || {};
      const lastImportId = importType === 'user' ? lastUserImport?.id : lastRetailerImport?.id;
      const id = dataArrayIds[lastImportIndex] || lastImportId;

      if (PROGRESS_STATES.includes(attributes?.status)) {
        clearInterval(importType === 'user' ? userIntervalIdRef.current : retailerIntervalIdRef.current);
        const toastId = notification.importStatus({
          closeProgressBar: importType === 'user' ? handleCloseUserProgressBar : handleCloseRetailerProgressBar,
          status: attributes.status,
          filename: attributes?.filename,
          id,
          type: importType,
        });
        if (importType === 'user') {
          await dispatch(setUserToastId(toastId));
        } else {
          await dispatch(setRetailerToastId(toastId));
        }
      }
      setIsProgressOpen(true);
    } catch (error) {
      // Non-fatal: progress polling continues on next interval tick.
    }
  };

  useEffect(() => {
    if (
      !isEmpty(usersImportInfo?.data) &&
      !isIdInLocalStorage(usersImportInfo?.data) &&
      isEmpty(lastUserImport) &&
      isAuthenticated
    ) {
      checkImportStatus(getUsersImportProgress, setIsUserProgressOpen, 'user');
    }
    if (
      !isEmpty(retailersImportInfo?.data) &&
      !isIdInLocalStorage(retailersImportInfo?.data) &&
      isEmpty(lastRetailerImport) &&
      isAuthenticated
    ) {
      checkImportStatus(getRetailersImportProgress, setIsRetailerProgressOpen, 'retailer');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersImportInfo?.data?.length, retailersImportInfo?.data?.length]);

  useEffect(() => {
    if (!isEmpty(lastUserImport) && !PROGRESS_STATES.includes(lastUserImport?.attributes?.status) && isAuthenticated) {
      userIntervalIdRef.current = setInterval(
        () => checkImportStatus(getUsersImportProgress, setIsUserProgressOpen, 'user'),
        5000,
      );
    }
    if (
      !isEmpty(lastRetailerImport) &&
      !PROGRESS_STATES.includes(lastRetailerImport?.attributes?.status) &&
      isAuthenticated
    ) {
      retailerIntervalIdRef.current = setInterval(
        () => checkImportStatus(getRetailersImportProgress, setIsRetailerProgressOpen, 'retailer'),
        5000,
      );
    }
    return () => {
      clearInterval(retailerIntervalIdRef.current);
      clearInterval(userIntervalIdRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastRetailerImport, lastUserImport]);

  const handleResetImportInfo = async (retailerInterval, userInterval) => {
    try {
      if (!preventNotificationDismiss) {
        toast.dismiss();
      }
      await dispatch(resetUserImportInfo());
      await dispatch(resetRetailerImportData());
      clearInterval(retailerInterval);
      clearInterval(userInterval);
    } catch (error) {
      // Non-fatal: intervals are still cleared below; reset retried on next auth change.
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      handleResetImportInfo(retailerIntervalIdRef.current, userIntervalIdRef.current);
    }
    return () => {
      if (!isAuthenticated) {
        handleResetImportInfo(retailerIntervalIdRef.current, userIntervalIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return (
    <Styled.Wrapper
      className="body_wrapper"
      id="html-canvas"
      $railCollapsed={railCollapsed}
      $isAuthenticated={isAuthenticated}
      $newUi={newUi}
    >
      {newUi ? <Navigation /> : <Header />}
      {isRetailerProgressOpen && isAuthenticated && (
        <ProgressBar importInfo={lastRetailerImport} onClose={handleCloseRetailerProgressBar} />
      )}
      {isUserProgressOpen && isAuthenticated && (
        <ProgressBar importInfo={lastUserImport} onClose={handleCloseUserProgressBar} />
      )}
      {isAuthenticated && !isEmpty(mediaExportInfo) && (
        <Styled.StickyProgress>
          <ProgressBar importInfo={mediaExportInfo} onClose={handleCloseMediaExport} download />
        </Styled.StickyProgress>
      )}
      <Styled.BodyContainer className="body_container" style={styles}>
        {children}
      </Styled.BodyContainer>
      <Footer />
    </Styled.Wrapper>
  );
}

PageContainer.propTypes = {
  preventNotificationDismiss: PropTypes.bool,
  styles: PropTypes.object,
};

PageContainer.defaultProps = {
  preventNotificationDismiss: false,
  styles: {},
};

export default PageContainer;
